<script>
import getErrorMessage from '@/modules/server-errors';
import { prettyURL } from '@/modules/prettifier';

export default {
  name: 'PermissionCard',
  props: {
    permission: Object
  },
  data() {
    return {
      dialogVisible: false,
      hasAllPages: !Array.isArray(this.permission.pages),
      isLoading: false,
      lastIndex: 0,
      availablePages: [],
      selectedPages: Array.isArray(this.permission.pages) ? this.permission.pages : []
    };
  },
  computed: {
    prettyURL() {
      return prettyURL(this.permission.site.url);
    }
  },
  methods: {
    // Closes the pages dialog
    closePagesDialog() {
      this.dialogVisible = false;
      this.availablePages = [];
      this.selectedPages = Array.isArray(this.permission.pages) ? this.permission.pages : [];
    },

    // Opens the pages dialog
    openPagesDialog() {
      this.dialogVisible = true;
      this.isLoading = true;
      this.$http.get(`/sites/${this.permission.site.id}/pages`, {
        params: {
          limit: 1000, // TODO - if > 1000 pages, loop until we get them all
          sortBy: 'name'
        }
      })
        .then(res => this.availablePages = res.data.pages)
        .catch(err => {
          this.$notify({
            type: 'error',
            title: this.$t('Permissions'),
            message: getErrorMessage(err.response.data.code)
          });
        })
        .finally(() => this.isLoading = false);
    },

    // Set pages
    setPages() {
      this.$emit('update', this.hasAllPages ? null : this.selectedPages);
      this.closePagesDialog();
    },

    // Called when the all pages radio is toggled
    toggleAllPages() {
      if (this.hasAllPages) {
        this.selectedPages = this.availablePages.map(p => p.id);
      } else {
        this.selectedPages = Array.isArray(this.permission.pages) ? this.permission.pages : [];
      }
    }
  }
};
</script>

<template>
  <div :data-id="permission.id" class="permission-card">
    <!-- Title -->
    <div class="permission-card-title">
      {{ permission.site.name }}
    </div>

    <!-- Description -->
    <div class="permission-card-description">
      {{ prettyURL }}
    </div>

    <!-- Controls -->
    <div>
      <el-button size="small" @click="openPagesDialog">
        {{ $t('Manage Pages') }}
      </el-button>

      <el-button type="danger" size="small" @click="$emit('revoke')">
        {{ $t('Revoke') }}
      </el-button>
    </div>

    <!-- Pages dialog -->
    <el-dialog
      :title="permission.site.name"
      :visible.sync="dialogVisible"
      @close="closePagesDialog"
    >
      <!-- All pages -->
      <el-form-item class="text-center">
        <el-radio-group v-model="hasAllPages" size="small" @input="toggleAllPages">
          <el-radio-button :label="true" :disabled="!availablePages.length" >
            {{ $t('All Pages') }}
          </el-radio-button>
          <el-radio-button :label="false" :disabled="!availablePages.length" >
            {{ $t('Selected Pages') }}
          </el-radio-button>
        </el-radio-group>
      </el-form-item>

      <!-- Page list -->
      <div v-loading="isLoading" class="permission-card-page-list">
        <el-checkbox-group
          v-if="availablePages.length"
          v-model="selectedPages"
          :disabled="hasAllPages"
        >
          <ul>
            <li v-for="(page, index) in availablePages" :key="index">
              <el-checkbox :label="page.id" :checked="hasAllPages">
                {{ page.name }}
                <small class="text-muted">{{ page.path }}</small>
              </el-checkbox>
            </li>
          </ul>
        </el-checkbox-group>
        <div v-else class="permission-card-page-list-empty">
          <div class="permission-card-page-list-empty-message">
            {{ $t('No Pages') }}
          </div>
        </div>
      </div>

      <!-- Dialog footer -->
      <div slot="footer">
        <!-- Cancel -->
        <el-button @click="dialogVisible = false">
          {{ $t('Cancel') }}
        </el-button>
        <!-- Apply -->
        <el-button type="primary" @click="setPages">
          {{ $t('Apply') }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.permission-card {
  border: solid 1px darken($--color-gray-10, 5%);
  box-shadow: $--box-shadow-small;
  border-radius: .25rem;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
}

.permission-card-title {
  margin-bottom: .25rem;
}

.permission-card-tag {
  float: right;
  margin-top: -1.25rem;

  @include breakpoint('sm-and-down') {
    float: none;
    margin: .25rem 0;
  }
}

.permission-card-description {
  font-size: $--font-size-small;
  color: $--color-gray-40;
  margin-bottom: 1rem;
}

.permission-card-page-list {
  border: solid 1px $--color-gray-20;
  border-radius: .25rem;
  background: $--color-white;
  font-size: 1rem;
  user-select: none;

  ul {
    height: 21.9rem; // ~ 10 items
    border-radius: inherit;
    overflow: auto;
    padding: 0;
    margin: 0;
  }

  li {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  label {
    display: block;
    padding: .5rem;
    cursor: pointer;

    &:hover {
      background-color: $--color-gray-10;
    }
  }
}

.permission-card-page-list-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 21.9rem;

  .permission-card-page-list-empty-message {
    color: $--color-gray-40;
    text-align: center;
  }
}
</style>
